import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import type { CouponDefinition } from '@/lib/api-admin';
import { throwFromResponse } from '../lib/apiErrorMessage';

/**
 * 회원 상세 "쿠폰 발급" 패널용 쿠폰 정의 조회.
 *
 * ★[coupon-audit] 종전 이 훅은 존재하지 않는 wire 형태({couponId, couponName, active,
 * discountType 'RATE', …})를 파싱했다 — 실제 BE(AdminCouponController →
 * CouponDefinitionResponse)는 {id, code, title, discountType FIXED|PERCENT, discountAmount,
 * validFrom/validTo, maxQuantity, totalIssued, …} 이고 `active` 필드가 아예 없어
 * `filter(c => c.active)` 가 항상 전부 걸러냈다(패널이 영구 "쿠폰 없음" = 기능 사망).
 * 정본 CouponDefinition(api-admin paths.ts)을 그대로 파싱하고, 발급 가능 여부는 FE 에서
 * 파생한다: 미만료(validTo) && 발급 쿼터 잔여(maxQuantity-totalIssued).
 */
export interface IssuableCouponView {
  couponId: string;
  couponCode: string;
  couponName: string;
  discountType: 'FIXED' | 'PERCENT';
  discountValue: number;
  /** 유효 종료일(validTo). null = 무기한. */
  endDate: string | null;
  /** 발급 가능 여부(파생) — 미만료 + 쿼터 잔여. BE issue 도 동일 쿼터를 검증한다. */
  issuable: boolean;
}

interface CouponListEnvelope {
  success: boolean;
  data?: { content?: CouponDefinition[]; items?: CouponDefinition[] } | null;
}

const toIssuableView = (c: CouponDefinition): IssuableCouponView => {
  const expired = c.validTo != null && new Date(c.validTo).getTime() < Date.now();
  const quotaLeft = c.maxQuantity == null || c.totalIssued < c.maxQuantity;
  return {
    couponId: c.id,
    couponCode: c.code,
    couponName: c.title,
    discountType: c.discountType,
    discountValue: c.discountAmount,
    endDate: c.validTo ?? null,
    issuable: !expired && quotaLeft,
  };
};

export const couponDefsQueryKey = () => ['admin', 'coupon-defs'] as const;

export const useCouponDefsQuery = () =>
  useQuery<IssuableCouponView[]>({
    queryKey: couponDefsQueryKey(),
    queryFn: async () => {
      const res = await adminFetch('/api/admin/v1/coupons?size=200');
      if (!res.ok) await throwFromResponse(res, '쿠폰 목록을 불러오지 못했습니다.');
      const env = (await res.json()) as CouponListEnvelope;
      // Spring Page 직렬화(content) 정본 + items 방어 폴백.
      const rows = env.data?.content ?? env.data?.items ?? [];
      return rows.map(toIssuableView);
    },
    staleTime: 60 * 1000,
  });

export const useCouponIssueMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ couponId, userId }: { couponId: string; userId: string }) => {
      const res = await adminFetch(`/api/admin/v1/coupons/${couponId}/issue`, {
        method: 'POST',
        body: JSON.stringify({ all: false, userIds: [userId] }),
      });
      if (!res.ok) await throwFromResponse(res, '쿠폰 발급에 실패했습니다.');
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: couponDefsQueryKey() });
    },
  });
};
